import React, { useState } from 'react';
import { X, Crown, Zap, Shield, Users, Globe, CheckCircle, ArrowRight } from 'lucide-react';
import { StripeService } from '../services/stripeService';
import { STRIPE_PRODUCTS } from '../stripe-config';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason?: string;
  currentPlan?: string;
  suggestedPlan?: 'pro' | 'enterprise';
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  reason,
  currentPlan = 'free',
  suggestedPlan = 'pro'
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpgrade = async (planName: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const product = STRIPE_PRODUCTS.find(p => p.name === planName);
      if (!product) {
        throw new Error(`${planName} not found`);
      }

      const { url } = await StripeService.createCheckoutSession(
        product.priceId,
        product.mode
      );

      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Upgrade failed:', error);
      
      // Check if the error is authentication-related
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Authentication required') || errorMessage.includes('Failed to authenticate user')) {
        setError('Your session has expired or is invalid. Please log in again to continue with the upgrade.');
      } else {
        setError('Failed to start upgrade process. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const proFeatures = [
    'Unlimited rooms',
    'Maximum 50 participants per room',
    '5 broadcast links per room',
    '10 API keys',
    'Custom branding',
    'Analytics dashboard',
    'Priority support'
  ];

  const enterpriseFeatures = [
    'All Pro features',
    'Maximum 200 participants per room',
    'Unlimited broadcast links',
    'Unlimited API keys',
    'Dedicated account manager',
    'Advanced security features',
    'API access',
    'Custom integration support'
  ];

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                Upgrade Your Plan
              </h2>
              {reason && (
                <p className="text-slate-600 dark:text-slate-400 mt-1">{reason}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pro Plan */}
            <div className={`relative bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-6 text-white ${
              suggestedPlan === 'pro' ? 'ring-4 ring-blue-300 dark:ring-blue-600' : ''
            }`}>
              {suggestedPlan === 'pro' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">
                    RECOMMENDED
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Crown className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Pro Plan</h3>
                  <p className="text-blue-100">Perfect for growing teams</p>
                </div>
              </div>

              <div className="text-3xl font-bold mb-6">
                €4<span className="text-lg opacity-80">/month</span>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-600 dark:text-slate-400">
                    Unlimited statuses
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-600 dark:text-slate-400">
                    Maximum 200 participants per status
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-600 dark:text-slate-400">
                    Unlimited broadcast links
                  </span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {proFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-300 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleUpgrade('Pro Plan')}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-blue-600 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Upgrade to Pro
                  </>
                )}
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className={`relative bg-gradient-to-br from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-800 rounded-2xl p-6 text-white ${
              suggestedPlan === 'enterprise' ? 'ring-4 ring-slate-400 dark:ring-slate-500' : ''
            }`}>
              {suggestedPlan === 'enterprise' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">
                    RECOMMENDED
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Enterprise Plan</h3>
                  <p className="text-slate-300">For large organizations</p>
                </div>
              </div>

              <div className="text-3xl font-bold mb-6">
                €6<span className="text-lg opacity-80">/month</span>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-600 dark:text-slate-400">
                    Unlimited statuses
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-600 dark:text-slate-400">
                    Maximum 200 participants per status
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-600 dark:text-slate-400">
                    Unlimited broadcast links
                  </span>
                </div>
              </div>

              <button
                onClick={() => handleUpgrade('Enterprise Plan')}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <Users className="w-4 h-4" />
                    Upgrade to Enterprise
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Current Plan Info */}
          {currentPlan !== 'free' && (
            <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <Globe className="w-4 h-4" />
                <span>Current plan: {currentPlan === 'pro' ? 'Pro Plan' : 'Enterprise Plan'}</span>
              </div>
            </div>
          )}

          {/* Benefits Summary */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-200 dark:border-blue-700">
            <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">
              Why upgrade?
            </h4>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              <li className="flex items-center gap-2">
                <ArrowRight className="w-3 h-3 text-blue-500" />
                {currentPlan === 'free' ? 'Unlimited rooms and advanced customization' : 'Dedicated support and advanced security'}
              </li>
              <li className="flex items-center gap-2">
                <ArrowRight className="w-3 h-3 text-blue-500" />
                {currentPlan === 'free' ? 'Analytics dashboard and custom branding' : 'Custom integrations and API access'}
              </li>
              <li className="flex items-center gap-2">
                <ArrowRight className="w-3 h-3 text-blue-500" />
                {currentPlan === 'free' ? 'Priority support and better performance' : 'Account management'}
              </li>
              <li className="flex items-center gap-2">
                <ArrowRight className="w-3 h-3 text-blue-500" />
                Cancel anytime with no long-term commitment
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;